import React, { useState } from "react";
import { DEPARTURE_REASONS } from "./api";

// =============================================================
// RemoveMemberModal — collects a structured departure reason and
// (for "Other") notes. Shows the orphan-count warning up front so
// admins understand the downstream impact.
// =============================================================
export default function RemoveMemberModal({ node, member, orphanCount, onConfirm, onClose, submitting }) {
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const needsNotes = reason === "Other";
  const notesFilled = !!(notes && notes.trim());
  const canConfirm = !!reason && (!needsNotes || notesFilled) && !submitting;

  const displayName = member
    ? ((member.firstName || "") + " " + (member.lastName || "")).trim()
    : (node.memberId || "Occupant");

  return (
    <div className="modal-overlay" data-testid="remove-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Remove Member</div>
          <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="remove-close">×</button>
        </div>
        <div className="modal-body">
          {/* Member header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 10, marginBottom: 14 }}>
            {member?.photo ? (
              <img src={member.photo} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                {(member?.firstName?.[0] || "?") + (member?.lastName?.[0] || "")}
              </div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--kc-text)" }}>{displayName}</div>
              <div style={{ fontSize: 12, color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>#{node.memberNumber || "—"}</div>
              <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>
                Current slot: <strong style={{ color: "var(--kc-text)" }}>{node.slotRef}</strong> · {node.levelName}
              </div>
            </div>
          </div>

          {orphanCount > 0 && (
            <div data-testid="remove-orphan-warning" style={{ padding: 12, background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.40)", borderRadius: 10, marginBottom: 14, fontSize: 12, color: "#f59e0b", fontFamily: "'Montserrat',sans-serif" }}>
              ⚠ Removing this member will orphan <strong>{orphanCount}</strong> direct report{orphanCount === 1 ? "" : "s"} that need reassignment.
            </div>
          )}

          <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Departure reason *</label>
          <select
            data-testid="remove-reason-select"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="form-input"
            style={{ width: "100%", marginTop: 6, marginBottom: 14, height: 40, padding: "6px 10px" }}
          >
            <option value="">Select a reason…</option>
            {DEPARTURE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>

          {needsNotes && (
            <>
              <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Notes (required) *</label>
              <textarea
                data-testid="remove-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Provide a brief explanation…"
                className="form-input"
                style={{ width: "100%", marginTop: 6, minHeight: 80 }}
              />
            </>
          )}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} data-testid="remove-cancel">Cancel</button>
          <button
            type="button"
            data-testid="remove-confirm"
            disabled={!canConfirm}
            onClick={() => onConfirm(reason, needsNotes ? notes.trim() : null)}
            className="btn btn-sm"
            style={{
              background: canConfirm ? "#ff6b6b" : "rgba(255,107,107,0.30)",
              color: canConfirm ? "var(--kc-on-accent)" : "var(--kc-sub)",
              border: `1px solid ${canConfirm ? "#ff6b6b" : "rgba(255,107,107,0.30)"}`,
              fontWeight: 700, letterSpacing: "0.05em",
            }}
          >
            {submitting ? "Removing…" : "Confirm Removal"}
          </button>
        </div>
      </div>
    </div>
  );
}
